import { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  MRT_Cell,
  MRT_TableInstance,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_TableOptions
} from "material-react-table";
import { Box, useTheme } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useSnackbar } from "notistack";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { FleetManagementRow, VehicleBookingEventDto } from "../types/FleetManagement-row.types";
import { VehicleBlockingEvent } from "../types/FleetManagement.types";
import { commonLocalization } from "../../../functions/tableLocalization";
import { EditableCategoryListToolbar } from "../../Category/editable-category-list-toolbar.component";
import { Colleague } from "../../../features/Connections/types/Colleague.type";
import {
  FleetEventSlidePanelEditRow,
  renderBookingLocationEdit,
  renderBookingUserEdit,
  renderFleetEventActions,
  renderFleetEventEdit,
  renderFleetEventLocationCell,
  renderFleetEventUserCell,
  renderFleetEventTypeCell,
  renderFleetEventVehicleCell,
  renderFleetEventVehicleEdit,
  renderReasonEdit
} from "./partial/FleetManagementEventDetailTable.partial";
import { FleetManagementEventDetailTableRowActions } from "./partial/FleetManagementEventDetailTable-row-actions.partial";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { renderRequestedDate } from "../../BookingRequest/booking-requests-management.partial";
import { buildFleetEventData } from "./functions/buildFleetEventData.function";
import { doTimeRangesOverlap, toAffectedBookings } from "./functions/fleetEventOverlap.function";
import { sortFleetEvents, toIsoString } from "./functions/fleetEventSort.function";
import { filterFleetEventByUser } from "./functions/fleetEventUserFilter.function";
import { getBlockReasonValidationError } from "./functions/validateBlockReasonForRow.function";
import { validateStartEndForRowLogic } from "./functions/validateStartEndForRow.function";
import { validateUserForRowLogic } from "./functions/validateUserForRow.function";
import { BookingRequestsActiveRow } from "../../BookingRequest/typings/booking-requests.types";
import { BookOnBehalfFleetPayload } from "../../../hooks/Remote/Fleet/useRemoteBookOnBehalfFleetBooking";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import { calculateMaxEndDate } from "../../BookingForm/utils/booking.utils";
import { filterMRTEndDateProps } from "../../../features/Approval/booking-approval-hook.functions";
import { AffectedBookingsConfirmDialog } from "../AffectedBookingsDialog/AffectedBookingsConfirmDialog.component";
import { useFleetEventDetailTableEditingOptions } from "./hooks/useFleetEventDetailTableEditingOptions";
import { handleFleetRequestError } from "./functions/handleFleetRequestError.function";
import { getSeeditMrtRowSx, withSeeditMrtInitialState } from "../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../themes/use-seedit-mrt-table-options";
const CREATED_ROW_ID = "mrt-row-create";

export enum FleetEventType {
  BOOKING = "booking",
  BLOCKING = "blocking"
}

/** Minimal location for booking event (locationInventoryId + display name). */
export type FleetEventLocation = { id: number; name: string };

export interface FleetEventRow {
  id: string;
  eventType: FleetEventType;
  start: string; // ISO String
  end: string; // ISO String
  /** User the booking is for (only for eventType === "booking"). */
  user?: Colleague | null;
  /** User who created the booking (only for eventType === "booking"); when different from user, shown as "booked by" in table/tooltip. */
  userBookedBy?: Colleague | null;
  /** Selected location for booking (only for eventType === "booking"); used as locationInventoryId. */
  location?: FleetEventLocation | null;
  reason?: string;
  /** Backend blocking event id for PATCH/DELETE (only for eventType === "block") */
  blockingEventId?: number;
  /** Vehicle id for display and conflict check when showing all fleet */
  vehicleId?: number;
  /** Current vehicle odometer (used to prefill check-in dialog) */
  odometer?: number | null;
  /** Backend booking id for cancel/check-in/check-out (only for eventType === "booking") */
  bookingId?: number;
  /** When true, check-out is available (only for eventType === "booking") */
  isCheckedIn?: boolean;
  /** When true, the trip has been completed (check-in/return done). */
  isCheckedOut?: boolean;
  /** Vehicle inventory id for this booking (calendar / booking-events APIs). */
  vehicleInventoryId?: number;
  /** Optimistic row shown while a create save request is in flight. */
  isPending?: boolean;
}

/** Per-field validation errors for a row. Shown on the corresponding control (start/end picker, user, reason). */
export type RowValidationErrors = {
  start?: string;
  end?: string;
  user?: string;
  reason?: string;
};

/** Result of attempting to create a block or booking. Caller handles success/rejected/skipped. */
export type CreateResult =
  | { status: "success" }
  | { status: "rejected"; error?: unknown }
  | { status: "skipped" };

export type FleetManagementEventDetailTableProps = {
  isEditable: boolean;
  selectedRow: FleetManagementRow | null;
  users: Colleague[] | undefined;
  /** Locations available for booking (used for location dropdown when creating a booking). */
  locationOptions?: Array<{ id: number; name: string }>;
  allFleetData?: FleetManagementRow[];
  /** Blocking events from API (per-vehicle or calendar). When set, used instead of row.blockedEvents */
  blockingEvents?: VehicleBlockingEvent[];
  /** Booking events from API (per-vehicle). When set, used instead of row.occupiedBooking */
  bookingEvents?: VehicleBookingEventDto[];
  /** Vehicle id for creating new block (single-vehicle mode). When null, adding blocks may be disabled */
  vehicleIdForNewBlock?: number | null;
  /** Vehicle id for creating a booking on behalf (single-vehicle mode). When null, book-on-behalf is disabled */
  vehicleIdForNewBooking?: number | null;

  handleCheckIn: (bookingId: number, initialOdometer?: number | null) => void;
  handleCheckOut: (bookingId: number, initialOdometer?: number | null) => void;
  handleCancel: (bookingId: number) => void;
  onBookOnBehalf?: (payload: BookOnBehalfFleetPayload) => Promise<unknown>;
  onCreateBlock?: (
    vehicleId: number,
    payload: { start: string; end: string; reason?: string | null; createdBy?: string }
  ) => Promise<unknown>;
  onUpdateBlock?: (
    blockingEventId: number,
    payload: {
      start?: string;
      end?: string;
      reason?: string | null;
      vehicleId?: number;
    }
  ) => Promise<unknown>;
  /** Update vehicle booking times (PUT /booking-schedule/update-vehicle-booking-times). Called when saving edits to a booking row. */
  onUpdateBooking?: (
    bookingId: number,
    payload: {
      start: string;
      end: string;
      userId?: string;
      locationInventoryId?: number;
      reason?: string | null;
      vehicleId?: number;
    }
  ) => Promise<unknown>;
  onDeleteBlocks?: (blockingEventIds: number[]) => Promise<unknown>;
};

export function FleetManagementEventDetailTable({
  isEditable,
  selectedRow,
  users,
  locationOptions,
  allFleetData,
  blockingEvents,
  bookingEvents,
  vehicleIdForNewBlock,
  vehicleIdForNewBooking,
  handleCheckIn,
  handleCheckOut,
  handleCancel,
  onBookOnBehalf,
  onCreateBlock,
  onUpdateBlock,
  onUpdateBooking,
  onDeleteBlocks
}: Readonly<FleetManagementEventDetailTableProps>) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const {
    settings: { timezone },
    userInformation: { sub: loggedInUserId, company }
  } = useSelector((state: RootState) => state.login);

  const metaData = company?.meta;

  /** Max datetime for start/end pickers: respect company max advanced booking for vehicle. */
  const maxDateForVehicle = useMemo(() => {
    return DateTime.fromISO(
      calculateMaxEndDate({
        metaData,
        bookingMode: BookingType.VEHICLE
      })
    ).toJSDate();
  }, [metaData]);

  const theme = useTheme();

  const { dateFormat, timeFormat } = useTimeFormat();
  const localeFullFormat = dateFormat + " " + timeFormat;

  const [eventData, setEventData] = useState<FleetEventRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, RowValidationErrors>>({});
  const [pendingBlockCreate, setPendingBlockCreate] = useState<{
    draft: Partial<FleetEventRow>;
    resolvedVehicleId: number;
    start: string;
    end: string;
  } | null>(null);

  const [draftByRowId, setDraftByRowId] = useState<Record<string, Partial<FleetEventRow>>>({});
  const setDraftForRow = (rowId: string, patch: Partial<FleetEventRow>) => {
    setDraftByRowId(prev => ({ ...prev, [rowId]: { ...(prev[rowId] ?? {}), ...patch } }));
  };
  const clearDraftForRow = (rowId: string) => {
    setDraftByRowId(prev => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const handleReasonDraftChange = (rowId: string, patch: Partial<FleetEventRow>) => {
    setValidationErrors(prev => {
      const next = { ...prev };
      const row = next[rowId];
      if (row) {
        const r = { ...row };
        delete r.reason;
        if (Object.keys(r).length > 0) {
          next[rowId] = r;
        } else {
          delete next[rowId];
        }
      }
      return next;
    });
    setDraftForRow(rowId, patch);
  };

  const handleUserDraftChange = (rowId: string, patch: Partial<FleetEventRow>) => {
    setDraftForRow(rowId, patch);
    const shouldClearUserValidation =
      patch.user?.userId != null || patch.start != null || patch.end != null;
    if (shouldClearUserValidation) {
      setValidationErrors(prev => {
        const next = { ...prev };
        const row = next[rowId];
        if (row) {
          const r = { ...row };
          delete r.user;
          if (Object.keys(r).length > 0) {
            next[rowId] = r;
          } else {
            delete next[rowId];
          }
        }
        return next;
      });
    }
  };

  const resetTableCreateEditValidation = (rowId: string | undefined): void => {
    if (rowId == null) return;
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    clearDraftForRow(rowId);
  };

  /** Clear a single field's validation error when user focuses the control (equipment-list pattern). */
  const clearValidationErrorForField = (rowId: string, field: keyof RowValidationErrors): void => {
    /* istanbul ignore next */
    setValidationErrors(prev => {
      const next = { ...prev };
      const row = next[rowId];
      if (row) {
        const r = { ...row };
        delete r[field];
        if (Object.keys(r).length > 0) {
          next[rowId] = r;
        } else {
          delete next[rowId];
        }
      }
      return next;
    });
  };

  const applyPendingRowFromDraft = (
    draft: Partial<FleetEventRow>,
    resolvedVehicleId: number,
    start: string,
    end: string
  ) => {
    const eventType = draft.eventType ?? FleetEventType.BOOKING;
    const locationProp =
      eventType === FleetEventType.BOOKING && draft.location ? { location: draft.location } : {};
    const pendingRow: FleetEventRow = {
      id: `pending-${uuidv4()}`,
      eventType,
      start,
      end,
      user: draft.user,
      reason: draft.reason,
      vehicleId: resolvedVehicleId,
      isPending: true,
      ...locationProp
    };
    setEventData(prev => sortFleetEvents([...prev, pendingRow], timezone));
  };

  const clearPendingRows = () => {
    setEventData(prev => prev.filter(row => !row.isPending));
  };

  const getEventsForVehicle = (vehicleId: number): FleetEventRow[] =>
    eventData.filter(e => e.vehicleId === vehicleId);

  /* istanbul ignore next */
  const getOverlappingBookingEvents = (
    vehicleId: number,
    start: string,
    end: string,
    excludeRowId?: string
  ): FleetEventRow[] => {
    const existing = getEventsForVehicle(vehicleId);
    return existing.filter(
      e =>
        e.id !== excludeRowId &&
        e.eventType === FleetEventType.BOOKING &&
        doTimeRangesOverlap(start, end, e.start, e.end)
    );
  };

  const validateBlockReasonForRow = (rowId: string, reason: unknown): boolean => {
    const error = getBlockReasonValidationError(reason, {
      required: t("Reason is required for blocking events"),
      textOnly: t("Reason must be text only")
    });

    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [rowId]: { ...prev[rowId], reason: error }
      }));
      return false;
    }

    /* istanbul ignore next */
    setValidationErrors(prev => {
      const next = { ...prev };
      const row = next[rowId];
      if (row) {
        const r = { ...row };
        delete r.reason;
        if (Object.keys(r).length > 0) {
          next[rowId] = r;
        } else {
          delete next[rowId];
        }
      }
      return next;
    });
    return true;
  };

  /** Validates start and end are present and valid ISO dates. For booking/blocking create and edit. */
  const validateStartEndForRow = (
    rowId: string,
    startIso: string | undefined,
    endIso: string | undefined
  ): boolean => {
    const result = validateStartEndForRowLogic(startIso, endIso, {
      startRequired: t("_fleetEventDetail_startDateRequired"),
      endRequired: t("_fleetEventDetail_endDateRequired")
    });

    if (!result.valid) {
      setValidationErrors(prev => ({
        ...prev,
        [rowId]: { ...prev[rowId], [result.field]: result.message }
      }));
      return false;
    }

    /* istanbul ignore next */
    setValidationErrors(prev => {
      const next = { ...prev };
      const row = next[rowId];
      if (row) {
        const r = { ...row };
        delete r.start;
        delete r.end;
        if (Object.keys(r).length > 0) {
          next[rowId] = r;
        } else {
          delete next[rowId];
        }
      }
      return next;
    });
    return true;
  };

  /* istanbul ignore next */
  const validateUserForRow = (
    rowId: string,
    user: Colleague | null | undefined
  ): user is Colleague => {
    const result = validateUserForRowLogic(rowId, user);

    if (result.valid) {
      setValidationErrors(prev => {
        const next = { ...prev };
        const row = next[rowId];
        if (row) {
          const r = { ...row };
          delete r.user;
          if (Object.keys(r).length > 0) {
            next[rowId] = r;
          } else {
            delete next[rowId];
          }
        }
        return next;
      });
      return true;
    }
    setValidationErrors(prev => ({
      ...prev,
      [rowId]: { ...prev[rowId], user: t(result.errorMessage) }
    }));
    return false;
  };

  const getNoVehicleMessage = (eventType: FleetEventType): string =>
    eventType === FleetEventType.BLOCKING
      ? t("Select a vehicle first to add a blocking event")
      : t("Select a vehicle first to book on behalf");

  const resetEventDataToRemoteState = () => {
    const events = buildFleetEventData(
      users,
      allFleetData,
      selectedRow,
      bookingEvents,
      blockingEvents
    );
    setEventData(sortFleetEvents(events, timezone));
  };

  const tryHandleCreateBlock = async (
    rowId: string,
    newVal: Partial<FleetEventRow>,
    resolvedVehicleId: number,
    start: string,
    end: string
  ): Promise<CreateResult> => {
    if (!validateBlockReasonForRow(rowId, newVal.reason)) {
      return { status: "skipped" };
    }
    if (!onCreateBlock) return { status: "skipped" };

    return onCreateBlock(resolvedVehicleId, {
      start,
      end,
      reason: newVal.reason ?? null,
      ...(loggedInUserId && { createdBy: loggedInUserId })
    })
      .then((): CreateResult => ({ status: "success" }))
      .catch((error: unknown): CreateResult => ({ status: "rejected", error }));
  };

  const tryHandleCreateBooking = async (
    newVal: Partial<FleetEventRow>,
    resolvedVehicleId: number,
    start: string,
    end: string,
    rowId: string
  ): Promise<CreateResult> => {
    if (!onBookOnBehalf) return { status: "skipped" };

    const locationInventoryId = newVal.location?.id ?? selectedRow?.location?.id ?? undefined;
    const user = newVal?.user;
    if (!validateUserForRow(rowId, user)) return { status: "skipped" };

    // Convert UTC start/end to user's timezone (login state) for the create-booking payload.
    const tz = timezone ?? "UTC";
    const startDt = DateTime.fromISO(start).setZone(tz);
    const endDt = DateTime.fromISO(end).setZone(tz);

    return onBookOnBehalf({
      users: [{ userId: user.userId, bookingInventoryId: resolvedVehicleId }],
      startDate: startDt.toFormat("yyyy-MM-dd"),
      endDate: endDt.toFormat("yyyy-MM-dd"),
      startTime: startDt.toFormat("HH:mm"),
      endTime: endDt.toFormat("HH:mm"),
      bookingType: BookingType.VEHICLE,
      tripReason: newVal.reason,
      locationInventoryId
    })
      .then((): CreateResult => ({ status: "success" }))
      .catch((error: unknown): CreateResult => ({ status: "rejected", error }));
  };

  const tryHandleUpdateBlock = async (
    blockingEventId: number,
    payload: {
      start?: string;
      end?: string;
      reason?: string | null;
      vehicleId?: number;
    }
  ): Promise<CreateResult> => {
    if (!onUpdateBlock) return { status: "skipped" };

    return onUpdateBlock(blockingEventId, payload)
      .then((): CreateResult => ({ status: "success" }))
      .catch((error: unknown): CreateResult => ({ status: "rejected", error }));
  };

  const tryHandleUpdateBooking = async (
    bookingId: number,
    payload: {
      start: string;
      end: string;
      userId?: string;
      locationInventoryId?: number;
      reason?: string | null;
      vehicleId?: number;
    }
  ): Promise<CreateResult> => {
    if (!onUpdateBooking) return { status: "skipped" };
    return onUpdateBooking(bookingId, payload)
      .then((): CreateResult => ({ status: "success" }))
      .catch((error: unknown): CreateResult => ({ status: "rejected", error }));
  };

  const executeCreateForRow = async (
    createdId: string,
    newVal: Partial<FleetEventRow>,
    eventType: FleetEventType,
    resolvedVehicleId: number,
    start: string,
    end: string
  ): Promise<CreateResult> => {
    if (eventType === FleetEventType.BLOCKING) {
      const overlappingBookings = getOverlappingBookingEvents(resolvedVehicleId, start, end);
      if (overlappingBookings.length > 0 && !pendingBlockCreate) {
        setPendingBlockCreate({ draft: newVal, resolvedVehicleId, start, end });
        return { status: "skipped" };
      }
      return tryHandleCreateBlock(createdId, newVal, resolvedVehicleId, start, end);
    }
    return tryHandleCreateBooking(newVal, resolvedVehicleId, start, end, createdId);
  };

  const clearCreateRowState = (tableInstance: MRT_TableInstance<FleetEventRow>) => {
    tableInstance.setCreatingRow(null);
    clearDraftForRow(CREATED_ROW_ID);
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[CREATED_ROW_ID];
      return next;
    });
  };

  const clearEditRowState = (tableInstance: MRT_TableInstance<FleetEventRow>, rowId: string) => {
    tableInstance.setEditingRow(null);
    clearDraftForRow(rowId);
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const handleEditRowSave: MRT_TableOptions<FleetEventRow>["onEditingRowSave"] = async ({
    values,
    table,
    row
  }) => {
    const draft = draftByRowId[row.id] ?? {};
    const merged = { ...row.original, ...values, ...draft };

    const startIso = toIsoString(merged.start) ?? merged.start;
    const endIso = toIsoString(merged.end) ?? merged.end;

    if (!validateStartEndForRow(row.id, startIso, endIso)) return;

    if (merged.eventType === FleetEventType.BLOCKING) {
      if (!validateBlockReasonForRow(row.id, merged.reason)) return;
    }

    let result: CreateResult | null = null;

    if (
      row.original.eventType === FleetEventType.BLOCKING &&
      row.original.blockingEventId != null
    ) {
      result = await tryHandleUpdateBlock(row.original.blockingEventId, {
        start: startIso,
        end: endIso,
        reason: merged.reason ?? null,
        vehicleId: merged.vehicleId
      });
    } else if (
      row.original.eventType === FleetEventType.BOOKING &&
      row.original.bookingId != null
    ) {
      const bookingStart =
        row.original.isCheckedIn && row.original.start ? row.original.start : startIso;
      result = await tryHandleUpdateBooking(row.original.bookingId, {
        start: bookingStart as string,
        end: endIso as string,
        userId: merged.user?.userId,
        locationInventoryId: merged.location?.id,
        reason: merged.reason ?? null,
        vehicleId: merged.vehicleId
      });
    }

    if (result != null) {
      switch (result.status) {
        case "skipped":
          return;
        case "rejected": {
          handleFleetRequestError(result.error, enqueueSnackbar, t);
          resetEventDataToRemoteState();
          clearEditRowState(table, row.id);
          return;
        }
        case "success": {
          resetEventDataToRemoteState();
          clearEditRowState(table, row.id);
          return;
        }
      }
    }

    const updatedData = [...eventData];
    updatedData[row.index] = {
      ...merged,
      start: startIso ?? merged.start,
      end: endIso ?? merged.end
    };
    setEventData(updatedData);
    clearEditRowState(table, row.id);
  };

  const handleCreateRowSave: MRT_TableOptions<FleetEventRow>["onCreatingRowSave"] = async ({
    values,
    table,
    row
  }) => {
    const draft = draftByRowId[CREATED_ROW_ID] ?? {};
    const newVal = { ...row.original, ...values, ...draft };
    const eventType = newVal.eventType ?? FleetEventType.BOOKING;
    const startIso = toIsoString(newVal.start) ?? newVal.start;
    const endIso = toIsoString(newVal.end) ?? newVal.end;

    if (!validateStartEndForRow(CREATED_ROW_ID, startIso, endIso)) return;

    if (eventType === FleetEventType.BLOCKING) {
      if (!validateBlockReasonForRow(CREATED_ROW_ID, newVal.reason)) return;
    }

    const start = startIso as string;
    const end = endIso as string;

    const isAllEventsMode = Boolean(allFleetData && allFleetData.length > 0);
    const resolvedVehicleId = isAllEventsMode
      ? newVal.vehicleId ?? null
      : vehicleIdForNewBlock ?? vehicleIdForNewBooking ?? null;

    if (resolvedVehicleId == null) {
      enqueueSnackbar(getNoVehicleMessage(eventType), { variant: "warning" });
      return;
    }

    applyPendingRowFromDraft(newVal, resolvedVehicleId as number, start, end);

    const result = await executeCreateForRow(
      CREATED_ROW_ID,
      newVal,
      eventType,
      resolvedVehicleId as number,
      start,
      end
    );

    if (result.status === "skipped") {
      clearPendingRows();
      return;
    }

    if (result.status === "rejected") {
      handleFleetRequestError(result.error, enqueueSnackbar, t);
      resetEventDataToRemoteState();
      return;
    }

    setPendingBlockCreate(null);
    resetEventDataToRemoteState();
    clearCreateRowState(table);
  };

  const showVehicleColumn = Boolean(allFleetData && allFleetData.length > 0);

  const { enableEditing, enableRowSelection, enableRowActions } =
    useFleetEventDetailTableEditingOptions({
      isEditable,
      timezone
    });

  const columns = useMemo<MRT_ColumnDef<FleetEventRow>[]>(
    () => [
      ...(isEditable
        ? [
            {
              accessorKey: "fleetActions",
              header: "",
              Cell: ({ row }) =>
                renderFleetEventActions({
                  row,
                  timezone,
                  checkinButtonLabel: t("fleet_checkIn"),
                  checkoutButtonLabel: t("fleet_checkOut"),
                  cancelButtonLabel: t("_fleetEventDetail_cancelButton"),
                  handleCheckIn,
                  handleCheckOut,
                  handleCancel
                }),
              size: 30,
              enableEditing: false,
              enableColumnFilter: false
            } as MRT_ColumnDef<FleetEventRow>
          ]
        : []),
      {
        accessorKey: "eventType",
        header: t("Event Type"),
        editVariant: "select",
        enableEditing,
        Cell: ({ row }) => renderFleetEventTypeCell(row.original.eventType, t),
        muiEditTextFieldProps: ({ row }) => {
          const placeholder = t("Select event type");
          return {
            required: true,
            placeholder,
            SelectProps: { displayEmpty: true },
            onChange: (e: { target: { value: unknown } }) => {
              const next = e.target.value as FleetEventType;
              setDraftForRow(row.id, { eventType: next });
              row.original.eventType = next;
              if (next !== FleetEventType.BOOKING) {
                row.original.user = null;
                row.original.location = null;
                setDraftForRow(row.id, { user: null, location: null });
              } else if (selectedRow?.location?.id != null) {
                const loc = {
                  id: selectedRow.location.id,
                  name: selectedRow.location.name ?? ""
                };
                row.original.location = loc;
                setDraftForRow(row.id, { location: loc });
              }
            }
          };
        },
        filterVariant: "multi-select",
        filterFn: "arrIncludesSome",
        filterSelectOptions: [
          { label: t("Booking"), value: FleetEventType.BOOKING },
          { label: t("Blocking"), value: FleetEventType.BLOCKING }
        ],
        editSelectOptions: [
          { label: t("Booking"), value: FleetEventType.BOOKING },
          { label: t("Blocking"), value: FleetEventType.BLOCKING }
        ]
      },
      ...(showVehicleColumn
        ? [
            {
              accessorKey: "vehicleId",
              header: t("Vehicle"),
              size: 180,
              enableEditing,
              enableColumnFilter: true,
              filterVariant: "select",
              filterSelectOptions: (allFleetData ?? []).map(f => ({
                label: f.name,
                value: f.id,
                key: String(f.id)
              })),
              Cell: ({ row }) => renderFleetEventVehicleCell(row.original.vehicleId, allFleetData),
              Edit: ({ row }) =>
                renderFleetEventVehicleEdit({
                  row,
                  allFleetData: allFleetData ?? [],
                  draft: draftByRowId[row.id],
                  onDraftChange: patch => setDraftForRow(row.id, patch)
                })
            } as MRT_ColumnDef<FleetEventRow>
          ]
        : []),
      {
        accessorKey: "start",
        accessorFn: row => new Date(row.start),
        header: t("Start Date & Time"),
        Cell: ({ cell }) =>
          renderRequestedDate(
            cell as unknown as MRT_Cell<BookingRequestsActiveRow, unknown>,
            localeFullFormat
          ),
        Edit: ({ row }) =>
          renderFleetEventEdit({
            row,
            title: "start",
            draft: draftByRowId[row.id],
            timezone,
            maxDate: maxDateForVehicle,
            validationError: validationErrors[row.id]?.start,
            onClearValidation: () => clearValidationErrorForField(row.id, "start"),
            onDraftChange: patch => setDraftForRow(row.id, patch)
          }),
        enableEditing,
        muiFilterDatePickerProps: {
          format: dateFormat.toUpperCase()
        },
        filterFn: "greaterThan",
        filterVariant: "date",
        sortingFn: "datetime"
      },
      {
        accessorKey: "end",
        accessorFn: row => new Date(row.end),
        header: t("End Date & Time"),
        sortingFn: "datetime",
        filterVariant: "date",
        filterFn: "lessThan",
        Cell: ({ cell }) =>
          renderRequestedDate(
            cell as unknown as MRT_Cell<BookingRequestsActiveRow, unknown>,
            localeFullFormat
          ),
        Edit: ({ row }) =>
          renderFleetEventEdit({
            row,
            title: "end",
            draft: draftByRowId[row.id],
            timezone,
            maxDate: maxDateForVehicle,
            validationError: validationErrors[row.id]?.end,
            onClearValidation: () => clearValidationErrorForField(row.id, "end"),
            onDraftChange: patch => setDraftForRow(row.id, patch)
          }),
        enableEditing,
        muiFilterDatePickerProps: ({ column }) =>
          /* istanbul ignore next */ filterMRTEndDateProps(column, dateFormat)
      },
      {
        accessorKey: "user",
        header: t("Booking User"),
        enableEditing,
        filterFn: filterFleetEventByUser,
        Cell: ({ row }) =>
          renderFleetEventUserCell(row.original.user, loggedInUserId, isEditable, {
            isBlockingEvent: row.original.eventType === FleetEventType.BLOCKING,
            adminLabel: t("Admin"),
            userBookedBy: row.original.userBookedBy ?? undefined,
            bookedByLabel: t("_fleetEventDetail_bookedBy")
          }),
        Edit: ({ row }) =>
          renderBookingUserEdit({
            row,
            users,
            draft: draftByRowId[row.id],
            validationError: validationErrors[row.id]?.user,
            onClearValidation: () => clearValidationErrorForField(row.id, "user"),
            onDraftChange: patch => handleUserDraftChange(row.id, patch)
          })
      },
      {
        accessorKey: "location",
        header: t("Location"),
        enableEditing: row => !row.original.isCheckedIn,
        Cell: ({ row }) => renderFleetEventLocationCell(row.original.location),
        Edit: ({ row }) =>
          renderBookingLocationEdit({
            row,
            locationOptions,
            draft: draftByRowId[row.id],
            onDraftChange: patch => setDraftForRow(row.id, patch)
          })
      },
      {
        accessorKey: "reason",
        header: t("FleetEventReason"),
        enableEditing: true,
        Cell: ({ row }) => <>{row.original.reason ?? ""}</>,
        Edit: ({ row }) =>
          renderReasonEdit({
            row,
            draft: draftByRowId[row.id],
            validationError: validationErrors[row.id]?.reason,
            onClearValidation: () => clearValidationErrorForField(row.id, "reason"),
            onDraftChange: patch => handleReasonDraftChange(row.id, patch)
          }),
        muiEditTextFieldProps: { required: true }
      }
    ],
    [
      isEditable,
      users,
      locationOptions,
      selectedRow,
      allFleetData,
      showVehicleColumn,
      validationErrors,
      draftByRowId,
      timezone,
      maxDateForVehicle,
      dateFormat,
      t,
      handleCheckIn,
      handleCheckOut,
      handleCancel,
      handleUserDraftChange,
      loggedInUserId,
      localeFullFormat,
      enableEditing
    ]
  );

  useEffect(() => {
    const events = buildFleetEventData(
      users,
      allFleetData,
      selectedRow,
      bookingEvents,
      blockingEvents
    );

    setEventData(sortFleetEvents(events, timezone));
  }, [selectedRow, allFleetData, timezone, users, blockingEvents, bookingEvents]);
  const seeditMrtOptions = useSeeditMrtTableOptions<FleetEventRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns,
    data: eventData,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    positionToolbarAlertBanner: "none",
    enableEditing: isEditable,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    autoResetPageIndex: false,
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 }
    }),
    getRowId: row => row.id,
    enableRowSelection,
    enableRowActions,
    onCreatingRowSave: handleCreateRowSave,
    onCreatingRowCancel: ({ row }) => resetTableCreateEditValidation(row?.id),
    onEditingRowSave: handleEditRowSave,
    onEditingRowCancel: ({ row }) => resetTableCreateEditValidation(row?.id),
    muiTableBodyRowProps: ({ row }) => {
      const seeditRowSx = getSeeditMrtRowSx(theme, { row });
      if (row.original.isPending) {
        return { sx: { ...seeditRowSx, opacity: 0.65, fontStyle: "italic" } };
      }
      return { sx: seeditRowSx };
    },
    muiTableContainerProps: {
      sx: { maxHeight: "calc(80vh - 250px)" }
    },
    renderRowActions: isEditable
      ? ({ row, table }) => (
          <FleetManagementEventDetailTableRowActions row={row} table={table} timezone={timezone} />
        )
      : undefined,
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <FleetEventSlidePanelEditRow
        internalEditComponents={internalEditComponents}
        headline={t("_fleetEventSlideout_editTitle")}
        table={table}
        row={row}
      />
    ),
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <FleetEventSlidePanelEditRow
        internalEditComponents={internalEditComponents}
        headline={t("Add new event")}
        table={table}
        row={row}
      />
    ),
    muiPaginationProps: { rowsPerPageOptions: [10, 20, 30], variant: "outlined" },
    renderTopToolbar: isEditable
      ? ({ table }) => {
          const selectedRows = table.getSelectedRowModel().rows;
          const selectedBlockIds = selectedRows
            .filter(
              r =>
                r.original.eventType === FleetEventType.BLOCKING &&
                r.original.blockingEventId != null
            )
            .map(r => r.original.blockingEventId!);
          const canDeleteBlocks = onDeleteBlocks != null && selectedBlockIds.length > 0;

          return (
            <EditableCategoryListToolbar
              table={table as unknown as MRT_TableInstance<any>}
              addItemLabel={t("Add new event")}
              disabledDeleteButton={selectedRows.length <= 0}
              onDelete={() => {
                if (canDeleteBlocks && onDeleteBlocks) {
                  onDeleteBlocks(selectedBlockIds);
                  table.resetRowSelection();
                }
              }}
            />
          );
        }
      : undefined,
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("There are no events")
    }
  });

  return (
    <Box sx={{ p: 1 }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MaterialReactTable table={table} />
      </LocalizationProvider>

      <AffectedBookingsConfirmDialog
        open={pendingBlockCreate != null}
        affectedBookings={
          pendingBlockCreate
            ? toAffectedBookings(
                getOverlappingBookingEvents(
                  pendingBlockCreate.resolvedVehicleId,
                  pendingBlockCreate.start,
                  pendingBlockCreate.end
                )
              )
            : []
        }
        vehicleName={
          pendingBlockCreate
            ? allFleetData?.find(item => item.id === pendingBlockCreate.resolvedVehicleId)?.name
            : undefined
        }
        allUsers={users}
        onCancel={() => setPendingBlockCreate(null)}
        onConfirm={async () => /* istanbul ignore next */ {
          const pending = pendingBlockCreate;
          if (!pending) return;
          applyPendingRowFromDraft(
            pending.draft,
            pending.resolvedVehicleId,
            pending.start,
            pending.end
          );
          const result = await tryHandleCreateBlock(
            CREATED_ROW_ID,
            pending.draft,
            pending.resolvedVehicleId,
            pending.start,
            pending.end
          );
          setPendingBlockCreate(null);
          switch (result.status) {
            case "skipped":
              clearPendingRows();
              return;
            case "rejected": {
              handleFleetRequestError(result.error, enqueueSnackbar, t);
              resetEventDataToRemoteState();
              return;
            }
            case "success":
              resetEventDataToRemoteState();
              clearCreateRowState(table);
          }
        }}
      />
    </Box>
  );
}
